import { useState, useEffect } from 'react';
import { templatesAPI } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { Layers, Plus, Trash2, Play, Save, X, CalendarDays, Users } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { format, startOfWeek, addDays, getISOWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function WeekTemplatesDialog({ open, onClose, currentWeekStart, onApplied }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [applyTemplate, setApplyTemplate] = useState(null);
  const [targetDate, setTargetDate] = useState(null);

  useEffect(() => {
    if (open) fetchTemplates();
  }, [open]);

  const fetchTemplates = async () => {
    try { const res = await templatesAPI.list(); setTemplates(res.data); } catch { /* */ }
  };

  const handleSave = async () => {
    if (!saveName.trim()) { toast.error('Entrez un nom'); return; }
    setLoading(true);
    try {
      await templatesAPI.save({
        name: saveName.trim(),
        week_start: format(currentWeekStart, 'yyyy-MM-dd'),
      });
      toast.success('Semaine type sauvegardée');
      setSaveName('');
      setShowSave(false);
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setLoading(false);
  };

  const handleApply = async () => {
    if (!applyTemplate || !targetDate) return;
    const targetWeekStart = startOfWeek(targetDate, { weekStartsOn: 1 });
    setLoading(true);
    try {
      const res = await templatesAPI.apply(applyTemplate.id, {
        target_week_start: format(targetWeekStart, 'yyyy-MM-dd'),
      });
      toast.success(res.data.message);
      setApplyTemplate(null);
      setTargetDate(null);
      onApplied?.(targetWeekStart);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce template ?')) return;
    try {
      await templatesAPI.delete(id);
      toast.success('Template supprimé');
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const weekNum = getISOWeek(currentWeekStart);
  const weekEnd = addDays(currentWeekStart, 6);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setApplyTemplate(null); setTargetDate(null); setShowSave(false); } onClose(); }}>
      <DialogContent data-testid="week-templates-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5" strokeWidth={1.5} />
            Semaines type
          </DialogTitle>
          <DialogDescription>Sauvegardez et réappliquez des plannings types</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template list */}
          <ScrollArea className="max-h-[220px]">
            <div className="space-y-2">
              {templates.map(t => (
                <div key={t.id} data-testid={`template-item-${t.id}`}
                  className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-900">{t.name}</span>
                        <Badge variant="secondary" className="text-[10px] h-5 px-1.5 font-data">
                          {t.shift_count} shifts
                        </Badge>
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-data">
                          {t.employee_count} emp.
                        </Badge>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Par {t.created_by} — {new Date(t.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600 hover:text-blue-800"
                        data-testid={`apply-template-${t.id}`}
                        onClick={() => { setApplyTemplate(t); setTargetDate(null); }}
                        title="Appliquer">
                        <Play className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700"
                        data-testid={`delete-template-${t.id}`}
                        onClick={() => handleDelete(t.id)}>
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {templates.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">Aucune semaine type sauvegardée</p>
              )}
            </div>
          </ScrollArea>

          <Separator />

          {/* Apply a template */}
          {applyTemplate && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3 animate-fade-in-up">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-blue-900">
                  Appliquer "{applyTemplate.name}"
                </Label>
                <button onClick={() => { setApplyTemplate(null); setTargetDate(null); }}
                  className="text-blue-400 hover:text-blue-600">
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>
              <p className="text-xs text-blue-700">Choisissez la semaine de destination :</p>
              <div className="flex justify-center">
                <Calendar
                  data-testid="template-apply-calendar"
                  mode="single"
                  selected={targetDate}
                  onSelect={setTargetDate}
                  locale={fr}
                  weekStartsOn={1}
                  className="rounded-lg border border-blue-200 bg-white"
                />
              </div>
              {targetDate && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-blue-700 font-data">
                    S. {getISOWeek(startOfWeek(targetDate, { weekStartsOn: 1 }))} — {format(startOfWeek(targetDate, { weekStartsOn: 1 }), 'dd/MM', { locale: fr })} au {format(addDays(startOfWeek(targetDate, { weekStartsOn: 1 }), 6), 'dd/MM', { locale: fr })}
                  </span>
                  <Button data-testid="confirm-apply-template-btn" size="sm" onClick={handleApply} disabled={loading}>
                    <Play className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
                    {loading ? 'Application...' : 'Appliquer'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Save current week */}
          {showSave ? (
            <div className="bg-slate-50 rounded-lg p-4 space-y-3 animate-fade-in-up">
              <Label className="text-sm font-semibold">
                Sauvegarder S.{weekNum} ({format(currentWeekStart, 'dd/MM', { locale: fr })} - {format(weekEnd, 'dd/MM', { locale: fr })})
              </Label>
              <Input data-testid="template-name-input" value={saveName}
                onChange={e => setSaveName(e.target.value)}
                placeholder='ex: "Semaine Type A"' />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => { setShowSave(false); setSaveName(''); }}>
                  Annuler
                </Button>
                <Button data-testid="save-template-btn" size="sm" onClick={handleSave} disabled={loading}>
                  <Save className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
                  {loading ? 'Enregistrement...' : 'Sauvegarder'}
                </Button>
              </div>
            </div>
          ) : !applyTemplate && (
            <Button data-testid="show-save-template-btn" variant="outline" className="w-full"
              onClick={() => setShowSave(true)}>
              <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Sauvegarder la semaine actuelle comme template
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
